import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, MousePointer, 
  Target, AlertCircle, CheckCircle, BarChart3, 
  ArrowUp, ArrowDown, Zap, Calendar, RefreshCw, Eye, Coins,
  History, ArrowRight, Clock, RotateCcw
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { formatNumber, formatCurrency, formatPercentage } from '../utils/formatting';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import OnboardingStickyCard from './OnboardingStickyCard';

// Greeting based on the visitor's *local* time (their own timezone).
function getTimeGreeting(date = new Date()) {
  const h = date.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function activityRelative(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

const SEVERITY_META = {
  critical: { color: '#ef4444', label: 'Critical' },
  high:     { color: '#f97316', label: 'High' },
  medium:   { color: '#f59e0b', label: 'Medium' },
  low:      { color: '#3b82f6', label: 'Low' },
  info:     { color: '#64748b', label: 'Info' }
};

function severityMeta(sev) {
  return SEVERITY_META[sev] || SEVERITY_META.medium;
}

// Recommendations carry impact as either a number (EUR) or a pre-formatted string
function formatImpactLabel(impact) {
  if (impact === null || impact === undefined || impact === '') return null;
  if (typeof impact === 'number') return `+€${Math.round(impact).toLocaleString('nl-NL')}`;
  return String(impact);
}

// Composite 0-100 "account health" score from the headline metrics.
function computeHealthScore(stats, budgetTracking) {
  const roas = Number(stats.ROAS) || 0;
  const ctr = Number(stats.avgCTR) || 0;
  const conv = Number(stats.totalConversions) || 0;
  const clicks = Number(stats.totalClicks) || 0;
  const convRate = clicks > 0 ? (conv / clicks) * 100 : 0;

  const roasScore = Math.min((roas / 4) * 100, 100);       // target ~4x
  const ctrScore = Math.min((ctr / 3) * 100, 100);          // ~3% benchmark
  const convScore = conv > 0 ? Math.min((convRate / 3) * 100, 100) : 20;

  let pacingScore = 70;
  const bt = budgetTracking?.budgetTracking;
  if (Array.isArray(bt) && bt.length > 0) {
    const onTrack = bt.filter(c => c.status === 'on_track').length;
    pacingScore = Math.round((onTrack / bt.length) * 100);
  }

  const score = Math.round(0.4 * roasScore + 0.2 * ctrScore + 0.2 * convScore + 0.2 * pacingScore);
  return Math.max(0, Math.min(100, score));
}

function healthMeta(score) {
  if (score >= 70) return { color: '#10b981', label: 'Healthy', desc: 'Your account is performing well across the board.' };
  if (score >= 40) return { color: '#f59e0b', label: 'Needs attention', desc: 'A few areas are dragging performance down.' };
  return { color: '#ef4444', label: 'At risk', desc: 'Several key metrics need action soon.' };
}

export default function Dashboard({ selectedAccount, onNavigate }) {
  const { theme } = useTheme();
  const { user } = useUser();
  const firstName = user?.firstName || (user?.fullName ? user.fullName.split(' ')[0] : '');
  const [greeting, setGreeting] = useState(getTimeGreeting());
  const [recentActivity, setRecentActivity] = useState([]);
  const [aiActions, setAiActions] = useState([]);
  const [aiLoading, setAiLoading] = useState(true);
  const [actionState, setActionState] = useState({}); // { [recId]: 'applying' | 'dismissing' }
  // Softer purple gradient in light mode (deeper indigo/violet 600-500 looks
  // too heavy on white backgrounds; use 400-tint variants instead).
  const purpleGradient = theme.mode === 'light'
    ? 'linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)'
    : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    totalCost: 0,
    totalClicks: 0,
    totalImpressions: 0,
    avgCTR: 0,
    totalConversions: 0,
    avgCPC: 0,
    ROAS: 0,
    CPA: 0
  });
  const [comparison, setComparison] = useState({});
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [dateRange, setDateRange] = useState(30);
  const [chartData, setChartData] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [budgetTracking, setBudgetTracking] = useState(null);
  const [accountHealth, setAccountHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [topPerformers, setTopPerformers] = useState([]);
  const [bottomPerformers, setBottomPerformers] = useState([]);
  const [error, setError] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);

  // Fetch data on mount and when account changes
  useEffect(() => {
    if (selectedAccount?.id) {
      console.log(`📊 Dashboard: Loading data for ${selectedAccount.name} (${selectedAccount.id})`);
      
      // IMPORTANT: Sync localStorage before fetching data
      // This ensures apiCall() uses the correct account
      const currentStored = localStorage.getItem('selectedGoogleAdsAccount');
      const currentStoredId = currentStored ? JSON.parse(currentStored).id : null;
      
      if (currentStoredId !== selectedAccount.id) {
        console.log(`🔄 Syncing localStorage: ${currentStoredId} → ${selectedAccount.id}`);
        localStorage.setItem('selectedGoogleAdsAccount', JSON.stringify(selectedAccount));
      }
      
      // Fetch data when component mounts or account changes
      fetchAllDashboardData();
    } else {
      console.log('📊 Dashboard: No account selected yet');
      setLoading(false); // Don't show loading if no account
    }
    // Note: We intentionally want this to run on every mount, so we include
    // an empty cleanup to ensure fresh data when navigating back to dashboard
    return () => {
      // Cleanup - reset loading state so next mount shows loading briefly
    };
  }, [selectedAccount?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedAccount?.id) {
      fetchAllDashboardData();
    }
  }, [dateRange, selectedLanguages]);

  // Keep the greeting accurate across time-of-day boundaries
  useEffect(() => {
    const t = setInterval(() => setGreeting(getTimeGreeting()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Track viewport width so the layout can stack the AI rail and keep KPIs
  // on a single row of four on laptop screens.
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchAllDashboardData = async () => {
    setError(null);
    setOverviewLoading(true);
    setTrendsLoading(true);
    setAccountHealth(null);
    
    // Fetch overview first (most important) - this removes the main loading screen
    try {
      await fetchDashboardOverview();
      setLoading(false); // Remove main loading screen once overview is ready
    } catch (error) {
      console.error('Error fetching overview:', error);
      setLoading(false);
    } finally {
      setOverviewLoading(false);
    }
    
    // Fetch the rest in parallel (non-blocking)
    Promise.all([
      fetchTrends().finally(() => setTrendsLoading(false)),
      fetchHeatmap(),
      fetchBudgetTracking(),
      fetchAccountHealth(),
      fetchTopPerformers(),
      fetchAlerts(),
      fetchRecentActivity(),
      fetchAiActions()
    ]).catch(error => {
      console.error('Error fetching secondary dashboard data:', error);
    });
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await apiCall(`/api/audit-log?limit=6`);
      const data = await response.json();
      if (data.success) {
        setRecentActivity(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };

  const fetchAiActions = async () => {
    setAiLoading(true);
    try {
      const [recsRes, dismissedRes, completedRes] = await Promise.all([
        apiCall('/api/optimization/all-recommendations', { timeout: 90000 }),
        apiCall('/api/optimization/dismissed'),
        apiCall('/api/optimization/completed')
      ]);
      const recsData = await recsRes.json();
      const dismissedData = await dismissedRes.json();
      const completedData = await completedRes.json();
      if (recsData.success) {
        const dismissed = new Set(dismissedData.dismissed || []);
        const completed = new Set(completedData.completed || []);
        const active = (recsData.recommendations || [])
          .filter(r => !dismissed.has(r.id) && !completed.has(r.id));
        setAiActions(active);
      }
    } catch (error) {
      console.error('Error fetching AI actions:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiAction = async (rec) => {
    setActionState(prev => ({ ...prev, [rec.id]: 'applying' }));
    try {
      const response = await apiCall('/api/optimization/apply', {
        method: 'POST',
        body: { recommendation: rec },
        timeout: 60000
      });
      const data = await response.json();
      if (data.success) {
        try {
          await apiCall('/api/optimization/track-applied', {
            method: 'POST',
            body: { recommendation: rec, accountId: rec.campaignId }
          });
        } catch (e) { /* non-fatal */ }
        setAiActions(prev => prev.filter(r => r.id !== rec.id));
        fetchRecentActivity();
      } else {
        setActionState(prev => ({ ...prev, [rec.id]: undefined }));
        setError(data.error || 'Could not apply this action');
      }
    } catch (err) {
      setActionState(prev => ({ ...prev, [rec.id]: undefined }));
      setError(err.message || 'Could not apply this action');
    }
  };

  const dismissAiAction = async (rec) => {
    setActionState(prev => ({ ...prev, [rec.id]: 'dismissing' }));
    try {
      await apiCall('/api/optimization/dismiss', {
        method: 'POST',
        body: { recommendation: rec, duration: 'forever' }
      });
    } catch (e) { /* non-fatal — still remove from view */ }
    setAiActions(prev => prev.filter(r => r.id !== rec.id));
  };

  const clearCacheAndRefresh = async () => {
    setLoading(true);
    try {
      // Clear all dashboard-related caches
      await apiCall(`/api/cache/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'all' })
      });
      
      // Wait a moment for cache to clear
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Now fetch fresh data
      await fetchAllDashboardData();
    } catch (error) {
      console.error('Error clearing cache:', error);
      setError('Unable to clear cache. Please try again.');
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await apiCall(`/api/dashboard/languages`);
      const data = await response.json();
      
      if (data.success && data.languages) {
        setAvailableLanguages(data.languages);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const getLanguageParam = () => {
    return selectedLanguages.length > 0 ? `&languages=${selectedLanguages.join(',')}` : '';
  };

  const fetchDashboardOverview = async () => {
    try {
      const response = await apiCall(`/api/dashboard/overview?days=${dateRange}${getLanguageParam()}`);
      const data = await response.json();
      
      if (data.success) {
        setCampaigns(data.campaigns || []);
        setStats(data.stats || {});
        setComparison(data.comparison || {});
      }
      
      if (data.apiLimited) {
        setError('⚠️ Google Ads API authentication issue. Some features may be limited.');
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    }
  };

  const fetchAccountHealth = async () => {
    setHealthLoading(true);
    try {
      const response = await apiCall(`/api/account-health?days=${dateRange}`);
      const data = await response.json();

      if (data.success) {
        setAccountHealth(data);
      }
    } catch (error) {
      console.error('Error fetching account health:', error);
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await apiCall(`/api/dashboard/trends?days=${dateRange}${getLanguageParam()}`);
      const data = await response.json();
      
      if (data.success) {
        setChartData(data.trends || []);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  };

  const fetchHeatmap = async () => {
    try {
      const response = await apiCall(`/api/dashboard/conversion-heatmap?days=${dateRange}`);
      const data = await response.json();
      if (data.success) {
        setHeatmap(data);
      }
    } catch (error) {
      console.error('Error fetching conversion heatmap:', error);
    }
  };

  const fetchBudgetTracking = async () => {
    try {
      const langParam = getLanguageParam();
      const url = langParam ? `/api/dashboard/budget-tracking?${langParam.substring(1)}` : `/api/dashboard/budget-tracking`;
      const response = await apiCall(url);
      const data = await response.json();
      
      if (data.success) {
        setBudgetTracking(data);
      }
    } catch (error) {
      console.error('Error fetching budget tracking:', error);
    }
  };

  const fetchTopPerformers = async () => {
    try {
      const langParam = getLanguageParam();
      const [topResponse, bottomResponse] = await Promise.all([
        apiCall(`/api/dashboard/top-performers?metric=roas&limit=5&direction=top${langParam}`),
        apiCall(`/api/dashboard/top-performers?metric=ctr&limit=5&direction=bottom${langParam}`)
      ]);
      
      const topData = await topResponse.json();
      const bottomData = await bottomResponse.json();
      
      if (topData.success) {
        setTopPerformers(topData.performers || []);
      }
      if (bottomData.success) {
        setBottomPerformers(bottomData.performers || []);
      }
    } catch (error) {
      console.error('Error fetching performers:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const langParam = getLanguageParam();
      const url = langParam ? `/api/dashboard/alerts?${langParam.substring(1)}` : `/api/dashboard/alerts`;
      const response = await apiCall(url);
      const data = await response.json();
      
      if (data.success) {
        setAlerts(data.alerts || []);
        setAlertsSummary(data.summary || { total: 0, high: 0, medium: 0, low: 0 });
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const toggleLanguage = (langCode) => {
    setSelectedLanguages(prev => {
      if (prev.includes(langCode)) {
        return prev.filter(code => code !== langCode);
      } else {
        return [...prev, langCode];
      }
    });
  };

  const clearLanguages = () => {
    setSelectedLanguages([]);
  };

  const StatCard = ({ icon: Icon, label, value, change, color, prefix = '', suffix = '' }) => (
    <div style={{
      background: theme.colors.bg.secondary,
      backdropFilter: 'blur(10px)',
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 16,
      padding: 22,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}>
          <Icon size={19} style={{ color: 'white' }} />
        </div>
        <p style={{
          color: theme.colors.text.secondary, fontSize: 13, fontWeight: 500,
          margin: 0, lineHeight: 1.2
        }}>
          {label}
        </p>
      </div>
      <p style={{ color: theme.colors.text.primary, fontSize: 26, fontWeight: 700, margin: '0 0 10px' }}>
        {prefix}{typeof value === 'number' ? formatNumber(value, prefix === '€' ? 2 : 0) : value}{suffix}
      </p>
      {change !== undefined && !isNaN(change) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {change > 0 ? (
            <ArrowUp size={15} style={{ color: '#10b981' }} />
          ) : change < 0 ? (
            <ArrowDown size={15} style={{ color: '#ef4444' }} />
          ) : null}
          <span style={{ 
            color: change > 0 ? '#10b981' : change < 0 ? '#ef4444' : theme.colors.text.secondary,
            fontSize: 13,
            fontWeight: 500
          }}>
            {change > 0 ? '+' : ''}{formatNumber(Math.abs(change), 1)}% vs previous period
          </span>
        </div>
      )}
    </div>
  );

  const healthToneColor = (toneOrSeverity) => {
    switch (toneOrSeverity) {
      case 'good':
      case 'low':
        return '#10b981';
      case 'warning':
      case 'medium':
        return '#f59e0b';
      case 'danger':
      case 'high':
        return '#f97316';
      case 'critical':
        return '#ef4444';
      default:
        return '#6366f1';
    }
  };

  const healthRouteMap = {
    optimization: 'bid-optimizer',
    'search-terms': 'search-term-analyzer',
    'quality-score': 'quality-score',
    'budget-allocator': 'budget-allocator',
    'account-audit': 'account-audit',
    campaigns: 'campaigns'
  };

  const navigateHealthAction = (route) => {
    const view = healthRouteMap[route] || route;
    if (view && typeof onNavigate === 'function') onNavigate(view);
  };

  const HealthDimensionCard = ({ dimension }) => {
    const color = healthToneColor(dimension.tone);
    return (
      <div style={{
        background: theme.colors.bg.secondary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: 16,
        padding: 18,
        minHeight: 150,
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <h4 style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 700, margin: '0 0 5px' }}>
              {dimension.name}
            </h4>
            <p style={{ color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.45, margin: 0 }}>
              {dimension.summary}
            </p>
          </div>
          <div style={{
            flex: '0 0 auto',
            minWidth: 54,
            padding: '6px 9px',
            borderRadius: 999,
            background: `${color}1A`,
            color,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 800
          }}>
            {dimension.score}
          </div>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: theme.mode === 'light' ? '#e5e7eb' : 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
          <div style={{
            width: `${Math.max(0, Math.min(100, dimension.score || 0))}%`,
            height: '100%',
            borderRadius: 999,
            background: color
          }} />
        </div>
        {dimension.drivers?.[0] && (
          <div style={{
            marginTop: 'auto',
            padding: '10px 12px',
            borderRadius: 12,
            background: theme.mode === 'light' ? '#f8fafc' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${theme.colors.border.primary}`
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <AlertCircle size={15} style={{ color: healthToneColor(dimension.drivers[0].severity), flex: '0 0 auto', marginTop: 1 }} />
              <div>
                <div style={{ color: theme.colors.text.primary, fontSize: 12, fontWeight: 700 }}>
                  {dimension.drivers[0].title}
                </div>
                <div style={{ color: theme.colors.text.secondary, fontSize: 12, lineHeight: 1.4, marginTop: 3 }}>
                  {dimension.drivers[0].description}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Tiny inline sparkline (no extra deps) — stretches to its container width.
  const Sparkline = ({ data = [], color = 'rgba(255,255,255,0.9)' }) => {
    const W = 100, H = 26;
    const pts = (data || []).map(Number).filter(v => !isNaN(v));
    if (pts.length < 2) return null;
    const min = Math.min(...pts);
    const max = Math.max(...pts);
    const range = max - min || 1;
    const stepX = W / (pts.length - 1);
    const coords = pts.map((v, i) => [i * stepX, H - ((v - min) / range) * (H - 2) - 1]);
    const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${line} L${W},${H} L0,${H} Z`;
    const gradId = `spark-grad-${Math.abs(pts.reduce((a, b, i) => a + b * (i + 1), 0)).toString(36).slice(0, 6)}`;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H}
        style={{ display: 'block', marginTop: 8, overflow: 'visible' }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradId})`} />
        <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={48} style={{ 
            color: '#6366f1', 
            marginBottom: 20,
            animation: 'spin 1s linear infinite'
          }} />
          <p>Loading dashboard...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  const hasBackendHealth = accountHealth?.score !== undefined && accountHealth?.score !== null;
  const healthScore = hasBackendHealth ? accountHealth.score : computeHealthScore(stats, budgetTracking);
  const fallbackHealthMeta = healthMeta(healthScore);
  const hm = hasBackendHealth
    ? {
        ...fallbackHealthMeta,
        color: healthToneColor(accountHealth.tone),
        label: accountHealth.label || fallbackHealthMeta.label,
        desc: accountHealth.summary || fallbackHealthMeta.desc
      }
    : fallbackHealthMeta;
  const ringCircumference = 2 * Math.PI * 52;
  const ringOffset = ringCircumference * (1 - healthScore / 100);

  // Responsive layout: below ~1500px the two-column layout squeezes the main
  // content too much, so we stack the AI rail underneath and give the hero +
  // KPI tiles the full width — keeping four KPIs on a single row.
  const stackRail = viewportWidth < 1500;
  const kpiColumns = viewportWidth < 700 ? 2 : 4;

  // Per-KPI daily series for the hero sparklines (derived from the trend data)
  const kpiSeries = {
    cost: chartData.map(d => d.cost || 0),
    conversions: chartData.map(d => d.conversions || 0),
    cpa: chartData.map(d => (d.conversions > 0 ? d.cost / d.conversions : 0)),
    ctr: chartData.map(d => d.ctr || 0)
  };

  // Supporting KPIs shown inside the hero
  const heroKpis = [
    { label: 'Spend', value: `€${formatNumber(stats.totalCost || 0, 0)}`, change: comparison.totalCost, series: kpiSeries.cost },
    { label: 'Conversions', value: formatNumber(stats.totalConversions || 0, 0), change: comparison.totalConversions, series: kpiSeries.conversions },
    { label: 'CPA', value: `€${formatNumber(stats.CPA || 0, 2)}`, change: undefined, series: kpiSeries.cpa },
    { label: 'CTR', value: `${stats.avgCTR || 0}%`, change: comparison.avgCTR, series: kpiSeries.ctr }
  ];

  return (
    <div style={{ padding: 40, maxWidth: 1600, margin: '0 auto' }}>
      {/* Onboarding welcome banner — auto-hides once dismissed, completed, or
          when the 14-day grace window has passed (server-gated). */}
      <OnboardingStickyCard onNavigate={onNavigate} />

      {/* Header with Date Range Selector */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ color: theme.colors.text.primary, fontSize: 32, fontWeight: 700, marginBottom: 10 }}>
              {greeting}{firstName ? `, ${firstName}` : ''}
            </h1>
            <p style={{ color: theme.colors.text.secondary, fontSize: 16 }}>
              {selectedAccount?.name
                ? `Here's what's happening with ${selectedAccount.name}`
                : 'Real-time insights across all your campaigns'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={fetchAllDashboardData}
              style={{
                background: purpleGradient,
                border: 'none',
                color: 'white',
                padding: '12px 24px',
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <RefreshCw size={18} />
              Refresh Data
            </button>
            <button
              onClick={clearCacheAndRefresh}
              title="Clear cache and fetch fresh data from Google Ads"
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                padding: '12px 20px',
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <Zap size={18} />
              Force Refresh
            </button>
          </div>
        </div>

        {/* Date Range Selector */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
          <Calendar size={20} style={{ color: theme.colors.text.secondary }} />
          {[7, 30, 90, 180].map(days => (
            <button
              key={days}
              onClick={() => setDateRange(days)}
              style={{
                background: dateRange === days 
                  ? purpleGradient
                  : theme.colors.bg.secondary,
                border: dateRange === days 
                  ? 'none' 
                  : `1px solid ${theme.colors.border.primary}`,
                color: dateRange === days ? 'white' : theme.colors.text.secondary,
                padding: '10px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: dateRange === days ? 600 : 400,
                transition: 'all 0.2s'
              }}
            >
              Last {days} Days
            </button>
          ))}
        </div>

        {/* Language Selector - Hidden for now to improve load speed */}
        {/* To re-enable: remove the false && condition below */}
        {false && availableLanguages.length > 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 12,
            padding: 20
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 15
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>🌍</span>
                <h3 style={{ color: theme.colors.text.primary, fontSize: 16, fontWeight: 600, margin: 0 }}>
                  Filter by Language
                </h3>
                {selectedLanguages.length > 0 && (
                  <span style={{
                    background: purpleGradient,
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600
                  }}>
                    {selectedLanguages.length} selected
                  </span>
                )}
              </div>
              {selectedLanguages.length > 0 && (
                <button
                  onClick={clearLanguages}
                  style={{
                    background: 'none',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: theme.colors.text.primary,
                    padding: '6px 12px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 12,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.05)';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'none';
                    e.target.style.color = 'rgba(255,255,255,0.7)';
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10
            }}>
              {availableLanguages.map(lang => (
                <label
                  key={lang.code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: selectedLanguages.includes(lang.code)
                      ? purpleGradient
                      : theme.colors.bg.secondary,
                    border: '1px solid ' + (selectedLanguages.includes(lang.code) 
                      ? 'transparent' 
                      : theme.colors.border.primary),
                    padding: '8px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    userSelect: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedLanguages.includes(lang.code)) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedLanguages.includes(lang.code)) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedLanguages.includes(lang.code)}
                    onChange={() => toggleLanguage(lang.code)}
                    style={{
                      width: 16,
                      height: 16,
                      cursor: 'pointer',
                      accentColor: '#6366f1'
                    }}
                  />
                  <span style={{ fontSize: 18, lineHeight: 1 }}>{lang.flag}</span>
                  <span style={{ 
                    color: 'white', 
                    fontSize: 14, 
                    fontWeight: selectedLanguages.includes(lang.code) ? 600 : 400
                  }}>
                    {lang.name}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 30,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <AlertCircle size={24} style={{ color: '#ef4444' }} />
          <div style={{ color: theme.colors.text.primary, fontSize: 14 }}>{error}</div>
        </div>
      )}

      {/* ===== Performance hero — account health score + headline ROAS ===== */}
      <div style={{
        background: purpleGradient,
        borderRadius: 24,
        padding: 32,
        marginBottom: 28,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 32,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Health ring */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: '0 0 auto' }}>
          <div style={{ position: 'relative', width: 120, height: 120 }}>
            <svg width={120} height={120}>
              <circle cx={60} cy={60} r={52} fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth={12} />
              <circle
                cx={60} cy={60} r={52} fill="none"
                stroke={hm.color} strokeWidth={12} strokeLinecap="round"
                strokeDasharray={ringCircumference}
                strokeDashoffset={ringOffset}
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease', filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.25))' }}
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center'
            }}>
              <span style={{ color: hm.color, fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{healthScore}</span>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600 }}>/ 100</span>
            </div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Account health
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: hm.color, boxShadow: `0 0 8px ${hm.color}` }} />
              <span style={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>{hm.label}</span>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, maxWidth: 220 }}>{hm.desc}</div>
          </div>
        </div>

        {/* ROAS headline */}
        <div style={{ flex: '0 0 auto', borderLeft: '1px solid rgba(255,255,255,0.25)', paddingLeft: 32 }}>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600 }}>Return on ad spend</div>
          <div style={{ color: '#fff', fontSize: 48, fontWeight: 800, lineHeight: 1.1 }}>
            {formatNumber(stats.ROAS || 0, 2)}x
          </div>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
            Last {dateRange} days
          </div>
        </div>

        {/* Supporting KPIs */}
        <div style={{
          flex: stackRail ? '1 1 100%' : '1 1 320px', display: 'grid',
          gridTemplateColumns: `repeat(${kpiColumns}, minmax(0, 1fr))`, gap: 16
        }}>
          {heroKpis.map(kpi => (
            <div key={kpi.label} style={{
              background: 'rgba(255,255,255,0.12)', borderRadius: 14, padding: '14px 16px'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, marginBottom: 4 }}>{kpi.label}</div>
              <div style={{ color: '#fff', fontSize: 22, fontWeight: 700 }}>{kpi.value}</div>
              {kpi.change !== undefined && !isNaN(kpi.change) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                  {kpi.change > 0 ? <ArrowUp size={13} style={{ color: '#d1fae5' }} /> : kpi.change < 0 ? <ArrowDown size={13} style={{ color: '#fecaca' }} /> : null}
                  <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                    {kpi.change > 0 ? '+' : ''}{formatNumber(Math.abs(kpi.change), 1)}%
                  </span>
                </div>
              )}
              <Sparkline data={kpi.series} color="rgba(255,255,255,0.85)" />
            </div>
          ))}
        </div>
      </div>

      {hasBackendHealth && (
        <div style={{
          background: theme.colors.bg.primary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 22,
          padding: 24,
          marginBottom: 32,
          boxShadow: theme.mode === 'light' ? '0 14px 40px rgba(15, 23, 42, 0.06)' : 'none'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 20,
            marginBottom: 20,
            flexWrap: 'wrap'
          }}>
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 999,
                background: `${hm.color}1A`,
                color: hm.color,
                fontSize: 12,
                fontWeight: 800,
                marginBottom: 10
              }}>
                <CheckCircle size={14} />
                Account Health 2.0
              </div>
              <h3 style={{ color: theme.colors.text.primary, fontSize: 21, fontWeight: 800, margin: '0 0 6px' }}>
                Why your account scores {healthScore}/100
              </h3>
              <p style={{ color: theme.colors.text.secondary, fontSize: 14, lineHeight: 1.55, margin: 0, maxWidth: 760 }}>
                {accountHealth.summary}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {healthLoading && (
                <span style={{ color: theme.colors.text.secondary, fontSize: 13, alignSelf: 'center' }}>
                  Refreshing health...
                </span>
              )}
              {(accountHealth.actions || []).slice(0, 3).map(action => (
                <button
                  key={`${action.route}-${action.label}`}
                  onClick={() => navigateHealthAction(action.route)}
                  style={{
                    background: theme.mode === 'light' ? '#f8fafc' : 'rgba(255,255,255,0.08)',
                    border: `1px solid ${theme.colors.border.primary}`,
                    color: theme.colors.text.primary,
                    padding: '9px 12px',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7
                  }}
                >
                  {action.label}
                  <ArrowRight size={14} />
                </button>
              ))}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
            marginBottom: accountHealth.drivers?.length ? 20 : 0
          }}>
            {(accountHealth.dimensions || []).map(dimension => (
              <HealthDimensionCard key={dimension.id} dimension={dimension} />
            ))}
          </div>

          {accountHealth.drivers?.length > 0 && (
            <div style={{
              borderTop: `1px solid ${theme.colors.border.primary}`,
              paddingTop: 18
            }}>
              <h4 style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 800, margin: '0 0 12px' }}>
                Main drivers
              </h4>
              <div style={{ display: 'grid', gap: 10 }}>
                {accountHealth.drivers.slice(0, 3).map((driver, index) => (
                  <div key={`${driver.dimension}-${driver.title}-${index}`} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 14,
                    background: theme.mode === 'light' ? '#f8fafc' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${theme.colors.border.primary}`
                  }}>
                    <div style={{
                      width: 26,
                      height: 26,
                      borderRadius: 9,
                      background: `${healthToneColor(driver.severity)}1A`,
                      color: healthToneColor(driver.severity),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto',
                      fontSize: 12,
                      fontWeight: 800
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 800 }}>
                        {driver.title}
                      </div>
                      <div style={{ color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.45, marginTop: 3 }}>
                        {driver.description}
                      </div>
                    </div>
                    {driver.route && (
                      <button
                        onClick={() => navigateHealthAction(driver.route)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#6366f1',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 800,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: 0,
                          flex: '0 0 auto'
                        }}
                      >
                        Fix
                        <ArrowRight size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== Two-column layout: main content + AI rail ===== */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
        {/* LEFT — main content */}
        <div style={{ flex: stackRail ? '1 1 100%' : '3 1 560px', minWidth: 0 }}>

      {/* Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(${kpiColumns}, minmax(0, 1fr))`,
        gap: 20,
        marginBottom: 40
      }}>
        <StatCard
          icon={Target}
          label="Active Campaigns"
          value={stats.totalCampaigns}
          change={comparison.totalCampaigns}
          color={purpleGradient}
        />
        <StatCard
          icon={MousePointer}
          label="Total Clicks"
          value={stats.totalClicks}
          change={comparison.totalClicks}
          color="linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)"
        />
        <StatCard
          icon={Coins}
          label="Avg. CPC"
          value={stats.avgCPC || 0}
          prefix="€"
          change={comparison.avgCPC}
          color="linear-gradient(135deg, #f97316 0%, #fb923c 100%)"
        />
        <StatCard
          icon={Eye}
          label="Impressions"
          value={stats.totalImpressions || 0}
          change={comparison.totalImpressions}
          color="linear-gradient(135deg, #14b8a6 0%, #5eead4 100%)"
        />
      </div>

      {/* Trend Charts */}
      {chartData.length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 24,
          marginBottom: 40
        }}>
          {/* Spend Over Time */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 20,
            padding: 30,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              Spend Over Time
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="rgba(255,255,255,0.6)" 
                  style={{ fontSize: 12 }}
                />
                <YAxis stroke="rgba(255,255,255,0.6)" style={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    background: theme.colors.bg.secondary, 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8
                  }}
                  labelStyle={{ color: 'white' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="cost" 
                  stroke="#6366f1" 
                  fillOpacity={1} 
                  fill="url(#colorCost)"
                  name="Spend (€)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Clicks & Impressions */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 20,
            padding: 30,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              Clicks & Impressions
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="rgba(255,255,255,0.6)"
                  style={{ fontSize: 12 }}
                />
                <YAxis stroke="rgba(255,255,255,0.6)" style={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    background: theme.colors.bg.secondary, 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8
                  }}
                  labelStyle={{ color: 'white' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#f59e0b" 
                  strokeWidth={2}
                  name="Clicks"
                />
                <Line 
                  type="monotone" 
                  dataKey="impressions" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Impressions"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* CTR Trend */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 20,
            padding: 30,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              CTR Trend
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCTR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="rgba(255,255,255,0.6)"
                  style={{ fontSize: 12 }}
                />
                <YAxis stroke="rgba(255,255,255,0.6)" style={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    background: theme.colors.bg.secondary, 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8
                  }}
                  labelStyle={{ color: 'white' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="ctr" 
                  stroke="#ec4899" 
                  fillOpacity={1} 
                  fill="url(#colorCTR)"
                  name="CTR (%)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Conversions */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 20,
            padding: 30,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              Conversions
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="rgba(255,255,255,0.6)"
                  style={{ fontSize: 12 }}
                />
                <YAxis stroke="rgba(255,255,255,0.6)" style={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    background: theme.colors.bg.secondary, 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8
                  }}
                  labelStyle={{ color: 'white' }}
                />
                <Bar 
                  dataKey="conversions" 
                  fill="#8b5cf6"
                  name="Conversions"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ===== Conversion heatmap: hour × day-of-week ===== */}
      {heatmap && heatmap.totalConversions > 0 && (
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 20,
          padding: 28,
          marginBottom: 40
        }}>
          <h3 style={{ color: theme.colors.text.primary, fontSize: 18, fontWeight: 600, margin: 0 }}>
            When do conversions happen?
          </h3>
          <p style={{ color: theme.colors.text.tertiary, fontSize: 13, margin: '4px 0 20px' }}>
            Conversions by hour and day of week · last {dateRange} days
          </p>
          <div style={{ overflowX: 'auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '40px repeat(24, minmax(16px, 1fr))',
              gap: 3,
              minWidth: 660
            }}>
              {/* Header row: corner + hour labels */}
              <div />
              {Array.from({ length: 24 }, (_, h) => (
                <div key={`h-${h}`} style={{
                  textAlign: 'center', fontSize: 9, color: theme.colors.text.tertiary,
                  paddingBottom: 4
                }}>
                  {h % 3 === 0 ? h : ''}
                </div>
              ))}
              {/* One row per day */}
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName, d) => [
                <div key={`d-${d}`} style={{
                  fontSize: 11, fontWeight: 600, color: theme.colors.text.secondary,
                  display: 'flex', alignItems: 'center'
                }}>
                  {dayName}
                </div>,
                ...Array.from({ length: 24 }, (_, h) => {
                  const cell = heatmap.cells[d * 24 + h] || { conversions: 0, convRate: 0, cost: 0 };
                  const intensity = heatmap.maxConversions > 0 ? cell.conversions / heatmap.maxConversions : 0;
                  return (
                    <div
                      key={`c-${d}-${h}`}
                      title={`${dayName} ${h}:00 — ${formatNumber(cell.conversions, 1)} conv · ${cell.convRate}% CR · €${formatNumber(cell.cost, 0)}`}
                      style={{
                        height: 22,
                        borderRadius: 4,
                        background: cell.conversions > 0
                          ? `rgba(139, 92, 246, ${(0.12 + intensity * 0.88).toFixed(3)})`
                          : theme.colors.bg.hover,
                        border: `1px solid ${theme.colors.border.secondary}`
                      }}
                    />
                  );
                })
              ])}
            </div>
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <span style={{ fontSize: 11, color: theme.colors.text.tertiary }}>Fewer</span>
            {[0.12, 0.35, 0.58, 0.8, 1].map((op, i) => (
              <span key={i} style={{
                width: 18, height: 12, borderRadius: 3,
                background: `rgba(139, 92, 246, ${op})`
              }} />
            ))}
            <span style={{ fontSize: 11, color: theme.colors.text.tertiary }}>More conversions</span>
          </div>
        </div>
      )}

      {/* Budget Tracking */}
      {budgetTracking && budgetTracking.budgetTracking && budgetTracking.budgetTracking.length > 0 && (
        <div style={{ marginBottom: 40 }}>
          <h2 style={{ color: 'white', fontSize: 24, fontWeight: 600, marginBottom: 20 }}>
            Budget Tracking
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 20 
          }}>
            {budgetTracking.budgetTracking.slice(0, 6).map(campaign => (
              <div key={campaign.campaignId} style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 16,
                padding: 20,
                border: `2px solid ${
                  campaign.status === 'overspending' ? '#ef4444' :
                  campaign.status === 'underspending' ? '#f59e0b' :
                  '#10b981'
                }`
              }}>
                <h4 style={{ color: 'white', marginBottom: 15, fontSize: 16, fontWeight: 600 }}>
                  {campaign.campaignName}
                </h4>
                <div style={{ marginBottom: 15 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: theme.colors.text.secondary, fontSize: 13 }}>Spent</span>
                    <span style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                      {formatCurrency(campaign.spent)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: theme.colors.text.secondary, fontSize: 13 }}>Daily Budget</span>
                    <span style={{ color: 'white', fontSize: 14 }}>
                      {formatCurrency(campaign.dailyBudget)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: theme.colors.text.secondary, fontSize: 13 }}>Remaining</span>
                    <span style={{ color: 'white', fontSize: 14 }}>
                      {formatCurrency(campaign.remaining)}
                    </span>
                  </div>
                </div>
                <div style={{ 
                  background: theme.colors.bg.hover, 
                  height: 8, 
                  borderRadius: 4, 
                  overflow: 'hidden',
                  marginBottom: 12
                }}>
                  <div style={{
                    background: campaign.status === 'overspending' ? '#ef4444' : 
                               campaign.status === 'underspending' ? '#f59e0b' : '#10b981',
                    height: '100%',
                    width: `${Math.min(100, campaign.pace * 100)}%`,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <p style={{ 
                  color: campaign.status === 'overspending' ? '#ef4444' : 
                         campaign.status === 'underspending' ? '#f59e0b' : '#10b981',
                  fontSize: 12,
                  fontWeight: 600
                }}>
                  {campaign.status === 'overspending' && '⚠️ Overspending'}
                  {campaign.status === 'underspending' && '⏸️ Underspending'}
                  {campaign.status === 'on_track' && '✓ On Track'}
                  {campaign.daysRemaining > 0 && ` · ${campaign.daysRemaining} days left`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top/Bottom Performers */}
      {(topPerformers.length > 0 || bottomPerformers.length > 0) && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
          gap: 24, 
          marginBottom: 40 
        }}>
          {/* Top Performers */}
          {topPerformers.length > 0 && (
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: 20, 
              padding: 30,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ 
                color: 'white', 
                marginBottom: 20, 
                fontSize: 20, 
                fontWeight: 600,
                display: 'flex', 
                alignItems: 'center', 
                gap: 10 
              }}>
                <TrendingUp size={24} style={{ color: '#10b981' }} />
                Top Performers (ROAS)
              </h3>
              {topPerformers.map((campaign, index) => (
                <div key={campaign.campaign?.id || index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < topPerformers.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      background: 'rgba(16, 185, 129, 0.2)',
                      color: '#10b981',
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 600
                    }}>
                      {index + 1}
                    </div>
                    <span style={{ color: 'white', fontSize: 14 }}>{campaign.campaign?.name}</span>
                  </div>
                  <span style={{ 
                    color: '#10b981', 
                    fontWeight: 600, 
                    fontSize: 16,
                    background: 'rgba(16, 185, 129, 0.1)',
                    padding: '4px 12px',
                    borderRadius: 6
                  }}>
                    {formatNumber(campaign.calculatedMetric, 2)}x
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {/* Bottom Performers */}
          {bottomPerformers.length > 0 && (
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: 20, 
              padding: 30,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ 
                color: 'white', 
                marginBottom: 20, 
                fontSize: 20, 
                fontWeight: 600,
                display: 'flex', 
                alignItems: 'center', 
                gap: 10 
              }}>
                <TrendingDown size={24} style={{ color: '#ef4444' }} />
                Needs Attention (CTR)
              </h3>
              {bottomPerformers.map((campaign, index) => (
                <div key={campaign.campaign?.id || index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < bottomPerformers.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 600
                    }}>
                      {index + 1}
                    </div>
                    <span style={{ color: 'white', fontSize: 14 }}>{campaign.campaign?.name}</span>
                  </div>
                  <span style={{ 
                    color: '#ef4444', 
                    fontWeight: 600, 
                    fontSize: 16,
                    background: 'rgba(239, 68, 68, 0.1)',
                    padding: '4px 12px',
                    borderRadius: 6
                  }}>
                    {formatNumber(campaign.calculatedMetric, 2)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Campaign Performance Table */}
      {campaigns.length > 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 20,
          padding: 30,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 20
          }}>
            <h3 style={{ color: 'white', fontSize: 20, fontWeight: 600 }}>
              Top Campaigns
            </h3>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '12px 16px',
                    color: theme.colors.text.secondary,
                    fontSize: 14,
                    fontWeight: 500
                  }}>Campaign</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', color: theme.colors.text.secondary, fontSize: 14, fontWeight: 500 }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '12px 16px', color: theme.colors.text.secondary, fontSize: 14, fontWeight: 500 }}>Clicks</th>
                  <th style={{ textAlign: 'right', padding: '12px 16px', color: theme.colors.text.secondary, fontSize: 14, fontWeight: 500 }}>CTR</th>
                  <th style={{ textAlign: 'right', padding: '12px 16px', color: theme.colors.text.secondary, fontSize: 14, fontWeight: 500 }}>Spend</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.slice(0, 10).map((campaign, index) => (
                  <tr key={campaign.campaign?.id || index} style={{ 
                    borderBottom: index < campaigns.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none' 
                  }}>
                    <td style={{ padding: '16px', color: 'white', fontSize: 14, fontWeight: 500 }}>
                      {campaign.campaign?.name}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        background: campaign.campaign?.status === 'ENABLED' 
                          ? 'rgba(16, 185, 129, 0.2)' 
                          : 'rgba(156, 163, 175, 0.2)',
                        color: campaign.campaign?.status === 'ENABLED' 
                          ? '#10b981' 
                          : '#9ca3af',
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 500
                      }}>
                        {campaign.campaign?.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: 14, textAlign: 'right' }}>
                      {formatNumber(campaign.metrics?.clicks || 0)}
                    </td>
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: 14, textAlign: 'right' }}>
                      {formatPercentage(campaign.metrics?.ctr || 0)}
                    </td>
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: 14, textAlign: 'right' }}>
                      {formatCurrency((campaign.metrics?.cost_micros || 0) / 1000000)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

        </div>{/* /LEFT column */}

        {/* RIGHT — AI rail */}
        <div style={{
          flex: stackRail ? '1 1 100%' : '1 1 320px', minWidth: stackRail ? 0 : 300,
          position: stackRail ? 'static' : 'sticky', top: 20,
          alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 20
        }}>
          {/* AI Actions */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)', borderRadius: 20, padding: 24,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: theme.colors.text.primary, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                <Zap size={20} style={{ color: '#f59e0b' }} />
                AI Actions
                {aiActions.length > 0 && (
                  <span style={{ background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>
                    {aiActions.length}
                  </span>
                )}
              </h3>
              <button
                onClick={() => onNavigate && onNavigate('bid-optimizer')}
                style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', fontSize: 13, fontWeight: 600 }}
              >
                View all <ArrowRight size={15} />
              </button>
            </div>

            {aiLoading ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: theme.colors.text.secondary }}>
                <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
                <p style={{ fontSize: 13, margin: 0 }}>Finding opportunities…</p>
              </div>
            ) : aiActions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <CheckCircle size={28} style={{ color: '#10b981', marginBottom: 8 }} />
                <p style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 600, margin: 0 }}>You're all caught up</p>
                <p style={{ color: theme.colors.text.secondary, fontSize: 12, margin: '4px 0 0' }}>No pending AI recommendations.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {aiActions.slice(0, 5).map(rec => {
                  const sm = severityMeta(rec.severity);
                  const impact = formatImpactLabel(rec.impact);
                  const busy = actionState[rec.id];
                  return (
                    <div key={rec.id} style={{
                      background: theme.colors.bg.tertiary, borderRadius: 12, padding: 14,
                      border: `1px solid ${theme.colors.border.primary}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: sm.color, marginTop: 6, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                            {rec.title}
                          </div>
                          {impact && (
                            <div style={{ color: '#10b981', fontSize: 12, fontWeight: 600, marginTop: 2 }}>{impact}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => applyAiAction(rec)}
                          disabled={!!busy}
                          style={{
                            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            padding: '7px 10px', borderRadius: 8, border: 'none',
                            background: theme.colors.brand.primary, color: '#fff', fontSize: 12, fontWeight: 600,
                            cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1
                          }}
                        >
                          {busy === 'applying' ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={13} />}
                          Apply
                        </button>
                        <button
                          onClick={() => dismissAiAction(rec)}
                          disabled={!!busy}
                          style={{
                            padding: '7px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border.primary}`,
                            background: 'transparent', color: theme.colors.text.secondary, fontSize: 12, fontWeight: 600,
                            cursor: busy ? 'wait' : 'pointer'
                          }}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Active alerts */}
          {alerts.length > 0 && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)', borderRadius: 20, padding: 24,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ color: theme.colors.text.primary, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 14px' }}>
                <AlertCircle size={20} style={{ color: '#ef4444' }} />
                Active Alerts ({alerts.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {alerts.slice(0, 5).map(alert => {
                  const col = alert.severity === 'high' ? '#ef4444' : alert.severity === 'medium' ? '#f59e0b' : '#3b82f6';
                  return (
                    <div key={alert.id} style={{
                      background: `${col}1A`, border: `1px solid ${col}55`, borderRadius: 10, padding: 12,
                      display: 'flex', gap: 10, alignItems: 'flex-start'
                    }}>
                      <AlertCircle size={16} style={{ color: col, marginTop: 1, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 500, margin: 0 }}>{alert.message}</p>
                        {alert.action && (
                          <p style={{ color: theme.colors.text.secondary, fontSize: 12, margin: '3px 0 0' }}>{alert.action}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent activity */}
          {recentActivity.length > 0 && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)', borderRadius: 20, padding: 24,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h3 style={{ color: theme.colors.text.primary, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                  <History size={20} style={{ color: '#06b6d4' }} />
                  Recent activity
                </h3>
                <button
                  onClick={() => onNavigate && onNavigate('audit-log')}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#06b6d4', fontSize: 13, fontWeight: 600 }}
                >
                  Log <ArrowRight size={15} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {recentActivity.slice(0, 5).map((entry, index, arr) => {
                  const reverted = entry.status === 'reverted';
                  return (
                    <div key={entry.id || index} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                      borderBottom: index < arr.length - 1 ? `1px solid ${theme.colors.border.secondary}` : 'none'
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: reverted ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {reverted ? <RotateCcw size={14} style={{ color: '#f59e0b' }} /> : <CheckCircle size={14} style={{ color: '#10b981' }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {entry.title}
                        </div>
                        <div style={{ color: theme.colors.text.tertiary, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={11} />
                          {activityRelative(reverted ? entry.revertedAt : entry.appliedAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>{/* /RIGHT rail */}
      </div>{/* /two-column */}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
